import * as React from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { DSGrid } from '@deltasierra/components';
import { BannerNotification as BannerNotificationDataType, BannerNotificationType } from '@deltasierra/shared';
import { Box, IconButton } from '@material-ui/core';
import { XClose } from '@untitled-ui/icons-react';
import { ExpressionBinding, OneWayBinding } from '../../common/angularData';
import { withAngularIntegration } from '../../common/componentUtils/reactComponentRegistration';
import { useBannerDismissal } from '../../common/hooks';
import { getBannerBackgroundColor, getBannerTextColor, getBannerIcon } from './utils';

const BannerContainer = styled(DSGrid)<{ type: BannerNotificationType }>`
    background-color: ${({ type }) => getBannerBackgroundColor(type)};
    color: ${({ type }) => getBannerTextColor(type)};
    display: flex;
    padding: 8px;
    text-align: center;
    position: relative;
    width: 100%;
    left: 0;
    top: 0;
    z-index: 1400;
    width: 100%;
    a {
        color: ${({ type }) => getBannerTextColor(type)};
        text-decoration: underline;
    }
`;

const NotificationMessageContainer = styled.div`
    margin: 0;
    display: flex;
    justify-content: center;
    align-items: 'center';
    p {
        margin: 0;
    }
`;

export interface BannerNotificationProps {
    bannerNotification: BannerNotificationDataType;
    onBannerDismiss?: (id: number) => void;
    preview?: boolean;
    setBannerNotificationElement?: (newElement: HTMLDivElement) => void;
}

export function BannerNotification({
    bannerNotification,
    onBannerDismiss,
    preview,
    setBannerNotificationElement,
}: BannerNotificationProps): JSX.Element {
    return (
        <BannerContainer
            alignItems="center"
            container
            direction="row"
            justifyContent="center"
            ref={setBannerNotificationElement}
            style={{ position: preview ? 'relative' : 'fixed' }}
            type={bannerNotification.type}
        >
            <Box sx={{ flex: 1 }}>
                {/** The message field should be properly sanitized on the server-side. See bannerNotificationService.ts */}
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        margin: 0,
                        width: 'auto',
                    }}
                >
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            marginRight: '12px',
                        }}
                    >
                        {getBannerIcon(bannerNotification.type)}
                    </Box>
                    <NotificationMessageContainer
                        dangerouslySetInnerHTML={{ __html: bannerNotification.message }}
                        style={{
                            padding: 0,
                        }}
                    />
                </Box>
            </Box>
            {bannerNotification.dismissable && (
                <Box
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        height: '15px',
                        justifyContent: 'end',
                        width: '15px',
                    }}
                >
                    <IconButton
                        disabled={preview}
                        style={{ padding: '5px' }}
                        onClick={() => {
                            onBannerDismiss?.(bannerNotification.id);
                        }}
                    >
                        <XClose
                            style={{
                                color: getBannerTextColor(bannerNotification.type),
                                cursor: 'pointer',
                                height: '20px',
                                padding: 0,
                                width: '20px',
                            }}
                        ></XClose>
                    </IconButton>
                </Box>
            )}
        </BannerContainer>
    );
}
BannerNotification.displayName = 'BannerNotification';

export interface BannerNotificationWrappedProps {
    bannerNotification: BannerNotificationDataType | null;
    onBannerDismiss: (id: number) => void;
    onUpdateBannerHeight: (newBannerHeight: number) => void;
}

export function BannerNotificationWrapped({
    bannerNotification,
    onBannerDismiss,
    onUpdateBannerHeight,
}: BannerNotificationWrappedProps): JSX.Element {
    const [bannerNotificationElement, setBannerNotificationElement] = useState<HTMLDivElement | null>(null);
    const [bannerHeight, setBannerHeight] = useState(0);

    const { dismissBanner, hasDismissedBanner } = useBannerDismissal(bannerNotification?.id);

    // Always show banner preview - used in admin panel
    const isDisplayBanner = !!bannerNotification && !hasDismissedBanner;

    useEffect(() => {
        if (!isDisplayBanner) {
            setBannerNotificationElement(null);
        }
    }, [isDisplayBanner]);

    useEffect(() => {
        if (!bannerNotificationElement) {
            setBannerHeight(0);
            return;
        }
        const updateBannerHeight = () => {
            setBannerHeight(bannerNotificationElement.offsetHeight);
        };

        updateBannerHeight();

        const resizeObserver = new ResizeObserver(updateBannerHeight);
        resizeObserver.observe(bannerNotificationElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [bannerNotificationElement]);

    useEffect(() => {
        onUpdateBannerHeight(bannerHeight);
    }, [bannerHeight, onUpdateBannerHeight]);

    return (
        <>
            {bannerNotification && isDisplayBanner && (
                <BannerNotification
                    bannerNotification={bannerNotification}
                    setBannerNotificationElement={setBannerNotificationElement}
                    onBannerDismiss={bannerId => {
                        dismissBanner();
                        onBannerDismiss?.(bannerId);
                    }}
                />
            )}
        </>
    );
}
BannerNotificationWrapped.displayName = 'BannerNotificationWrapped';

export const BannerNotificationAngular = withAngularIntegration(BannerNotificationWrapped, 'bannerNotification', {
    bannerNotification: OneWayBinding,
    onBannerDismiss: ExpressionBinding,
    onUpdateBannerHeight: ExpressionBinding,
});
